import { Toaster as Sonner, type ToasterProps, toast } from 'sonner'

/**
 * Toasts, styled to the design system.
 *
 * A toast takes the popover elevation (DESIGN.md §4): overlay paper, a 1px
 * `--border` edge, the popover shadow and 6px corners. It interrupts nothing,
 * so it never takes the modal shadow. The lead icon carries the signal colour
 * (success, warning, danger, info); the title is ink and the description
 * secondary ink. The action is a compact primary button and the cancel a
 * compact secondary one.
 *
 * Sonner does not read `dir`, so each app passes `position` from the active
 * direction (see its shell). The default here is the LTR one.
 *
 * Sonner renders into a portal outside the React tree; `toastOptions.classNames`
 * is the supported way to restyle it. The `!` marks beat the library's own
 * attribute-selector styles.
 */
function Toaster({ position = 'bottom-left', ...props }: ToasterProps) {
  return (
    <Sonner
      position={position}
      toastOptions={{
        classNames: {
          toast:
            'group !rounded-md !border !border-border !bg-popover !text-popover-foreground !shadow-popover gap-2.5 p-3',
          title: 'text-body font-medium text-foreground',
          description: '!text-small !text-muted-foreground',
          actionButton:
            '!h-control-compact !rounded-sm !border !border-primary !bg-primary !px-2.5 !text-label !text-primary-foreground',
          cancelButton:
            '!h-control-compact !rounded-sm !border !border-input !bg-card !px-2.5 !text-label !text-foreground',
          closeButton: '!border-border !bg-popover !text-muted-foreground hover:!bg-accent',
          icon: 'size-4',
          success: '[&_[data-icon]]:text-success',
          warning: '[&_[data-icon]]:text-warning',
          error: '[&_[data-icon]]:text-danger',
          info: '[&_[data-icon]]:text-info',
        },
      }}
      {...props}
    />
  )
}

export { Toaster, toast }
